import { css, Icon, mergeStyles, ScreenWidthMinLarge, Theme, ThemeContext } from '@fluentui/react';
import { Link } from '@fluentui/react/lib/components/Link';
import { ResponsiveMode } from '@fluentui/react/lib/utilities/decorators/withResponsiveMode';
import * as React from 'react';
import { CollapsibleToggle } from '@uifabric/portal-ux/lib/Collapsible';
import { Card } from '@uifabric/portal-ux/lib/components/Card';
import { gridUnit } from '@uifabric/portal-ux/lib/styling/stylingConstants';
import { TitleBarLabel } from '@uifabric/portal-ux/lib/TitleBar';
import { LocalizedCollapsible } from '../../shared/LocalizedCollapsible';
import { AccountActions } from '../models';
import { IPersonBadgeProps, IPersonBadgeState } from './PersonBadge.types';
import { CannotEditInfoDetails } from './CannotEditInfoDetails';
import { GetVerifyId } from '../Credential/GetVerifyId';
import { settingsManager } from '../../shared/settings';
import { Persona } from './Persona';

const cardStyles = require('@microsoft/portal-app/lib/styling/patterns/Card.scoped.scss');
const tileStyles = require('@microsoft/portal-app/lib/styling/patterns/Tile.scoped.scss');
const sectionStyles = require('@microsoft/portal-app/lib/styling/patterns/Section.scoped.scss');
const accountStyles = require('./PersonBadge.scoped.scss');

/**
 * The Account Home page represents the splash screen for the MyAccount page that will
 * contain all the tiles and general information about the account.
 */
export class PersonBadge extends React.Component<IPersonBadgeProps, IPersonBadgeState> {
  constructor(props: IPersonBadgeProps) {
    super(props);

    this.state = {
      isExpanded: false,
    };
  }

  public componentDidMount(): void {
    const { getUser } = this.props;
    getUser();
  }

  public render(): React.ReactNode {
    const { responsiveMode } = this.props;

    if (responsiveMode && responsiveMode >= ResponsiveMode.large) {
      return (
        <ThemeContext.Consumer>
          {(theme: Theme | undefined) => {
            return (
              <div className={css('ms-AccountInfo', cardStyles.card, cardStyles.cardColumn, accountStyles.badge)}>
                <div className={css(cardStyles.cardSectionContent, accountStyles.badgeContent)}>
                  <div className={css('ms-tile', tileStyles.tile)}>
                    <div className={tileStyles.header}>
                      <Persona
                        displayName={this.props.userPerson?.displayName}
                        tenantId={this.props.userProfile?.tid}
                      />
                      <div className={css('ms-tileTitle', tileStyles.tileTitle, 'ms-pii')}>
                        {this._renderDisplayName(theme)}
                      </div>
                      {this._renderJobTitleAndOffice(theme)}
                    </div>
                  </div>
                  {this._renderSectionContent(theme)}
                  {this._renderSignOutEverywhere()}
                </div>
              </div>
            );
          }}
        </ThemeContext.Consumer>
      );
    } else {
      return this._getCollapsedPersonBadge();
    }
  }

  private _onRenderHeader = () => {
    return (
      <ThemeContext.Consumer>
        {(theme: Theme | undefined) => {
          return (
            <>
              <TitleBarLabel
                styles={{
                  root: {
                    paddingLeft: gridUnit * 2,
                    paddingRight: gridUnit * 1.5,
                    height: gridUnit * 12,
                  },
                }}
              >
                <Persona displayName={this.props.userPerson?.displayName} tenantId={this.props.userProfile?.tid} />
              </TitleBarLabel>
              <TitleBarLabel
                styles={{
                  root: {
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                  },
                }}
              >
                {this._renderDisplayName(theme)}
                {this._renderJobTitleAndOffice(theme)}
              </TitleBarLabel>
            </>
          );
        }}
      </ThemeContext.Consumer>
    );
  };

  private _onRenderContent = () => {
    return (
      <ThemeContext.Consumer>
        {(theme: Theme | undefined) => {
          return (
            <div className={accountStyles.badgeContent}>
              {this._renderSectionContent(theme)}
              {this._renderSignOutEverywhere()}
            </div>
          );
        }}
      </ThemeContext.Consumer>
    );
  };

  private _getCollapsedPersonBadge(): React.ReactNode {
    const { t } = this.props;
    return (
      <Card className={accountStyles.personCard}>
        <LocalizedCollapsible
          name={t('Profile Overview')}
          toggle={CollapsibleToggle.header}
          onRenderHeader={this._onRenderHeader}
          onRenderContent={this._onRenderContent}
        />
      </Card>
    );
  }

  private _showSignOut = (): void => {
    const { t, showDialog } = this.props;

    if (showDialog !== undefined) {
      showDialog({
        title: t('confirmSignOut'),
        subText: t('areYouSure?'),
        okAction: AccountActions.submitSignOut,
        cancelAction: AccountActions.cancelSignOut,
      });
    }
  };

  private _renderDisplayName = (theme: Theme | undefined): React.ReactNode => {
    const { userPerson } = this.props;

    return (
      <div
        className={css(
          'ms-tileTitle',
          'ms-pii',
          theme &&
            mergeStyles(accountStyles.displayName, {
              fontSize: theme.fonts.xxLarge.fontSize,
              selectors: {
                [`@media (max-width: ${ScreenWidthMinLarge}px)`]: {
                  fontSize: theme?.fonts.mediumPlus.fontSize,
                },
              },
            }),
        )}
      >
        {userPerson && userPerson.displayName}
      </div>
    );
  };

  private _renderJobTitleAndOffice = (theme: Theme | undefined): React.ReactNode => {
    const { userPerson } = this.props;

    return (
      <div
        className={mergeStyles(
          accountStyles.jobTitleAndOffice,
          theme && {
            fontSize: theme.fonts.medium.fontSize,
            selectors: {
              [`@media (max-width: ${ScreenWidthMinLarge}px)`]: {
                fontSize: theme.fonts.xSmall.fontSize,
              },
            },
          },
        )}
      >
        <div className="ms-pii">{userPerson && userPerson.jobTitle}</div>
        <div className="ms-pii">{userPerson && userPerson.officeLocation}</div>
      </div>
    );
  };

  private _renderSectionContent = (theme: Theme | undefined): React.ReactNode => {
    const { userPerson } = this.props;
    const businessPhones = userPerson?.businessPhones;
    const telephoneNumber = businessPhones && businessPhones[0];
    const isVerifiedIDEnabled = settingsManager.get('isVerifiedIDEnabled');

    return (
      <div className={css('ms-section', sectionStyles.section)}>
        <div>
          {userPerson && (userPerson.mail || userPerson.userPrincipalName) ? (
            <div className={accountStyles.personaInfoContainer}>
              <Icon
                className={accountStyles.personaInfoIcon}
                data-testid="mail-icon"
                iconName={'mail'}
                style={{ fontSize: theme?.fonts.mediumPlus.fontSize }}
              />
              <div className={accountStyles.personaInfoText}>
                <span className="ms-pii">{userPerson.mail || userPerson.userPrincipalName}</span>
              </div>
            </div>
          ) : null}
        </div>
        <div>
          {userPerson && (telephoneNumber || userPerson.mobilePhone) ? (
            <div className={accountStyles.personaInfoContainer}>
              <Icon className={accountStyles.personaInfoIcon} iconName={'phone'} />
              <div className={accountStyles.personaInfoText}>
                <span className="ms-pii">{telephoneNumber || userPerson.mobilePhone}</span>
              </div>
            </div>
          ) : null}
        </div>
        <div>
          {userPerson && userPerson.city ? (
            <div className={accountStyles.personaInfoContainer}>
              <Icon className={accountStyles.personaInfoIcon} iconName={'CompassNW'} />
              <div className={accountStyles.personaInfoText}>
                <span className="ms-pii">{userPerson.city}</span>
              </div>
            </div>
          ) : null}
        </div>
        {userPerson && isVerifiedIDEnabled && <GetVerifyId userPerson={userPerson} />}
        <hr className={accountStyles.horizontalLine} />
        <div className={css('ms-section', accountStyles.label)}>
          <CannotEditInfoDetails />
        </div>
      </div>
    );
  };

  private _renderSignOutEverywhere = (): React.ReactNode => {
    const { t } = this.props;

    return (
      <div className={accountStyles.signOutEverywhereContent}>
        <div>
          <hr className={accountStyles.horizontalLine} />
          <Link onClick={this._showSignOut} className={accountStyles.link}>
            {t('signOutEverywhere')}
          </Link>
        </div>
      </div>
    );
  };
}
